import React, { useMemo } from 'react';
import { Search, Calendar, History, Settings as SettingsIcon, Trash2, ChevronLeft, ChevronRight, User as UserIcon, Coins } from 'lucide-react';
import { User } from '../../../types';
import { at } from '../../../i18nAdmin';
import { Language } from '../../../types';

interface UsersTabProps {
    lang: Language;
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    userFilterDate: string;
    setUserFilterDate: (date: string) => void;
    setUserCurrentPage: (page: number | ((prev: number) => number)) => void;
    paginatedUsers: User[];
    userCurrentPage: number;
    totalUserPages: number;
    handleViewUserDetail: (user: User) => void;
    openConfirm: (type: 'DELETE_USER' | 'UPDATE_ROLE' | 'AWARD_POINTS', data: any) => void;
}

export const UsersTab: React.FC<UsersTabProps> = ({
    lang,
    searchTerm,
    setSearchTerm,
    userFilterDate,
    setUserFilterDate,
    setUserCurrentPage,
    paginatedUsers,
    userCurrentPage,
    totalUserPages,
    handleViewUserDetail,
    openConfirm
}) => {
    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">{at(lang, 'personnel')}</h1>
                    <p className="text-sm font-medium text-slate-500 mt-1">{at(lang, 'personnelManageDesc')}</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
                        <Calendar size={14} className="text-rose-500" />
                        <input 
                            type="date" 
                            className="bg-transparent text-[11px] font-black uppercase text-slate-600 outline-none cursor-pointer"
                            value={userFilterDate}
                            onChange={e => { setUserFilterDate(e.target.value); setUserCurrentPage(1); }}
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-8 border-b border-slate-100">
                    <div className="relative group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input 
                            type="text" 
                            placeholder={at(lang, 'searchPersonnel')} 
                            className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-4 text-sm font-bold text-slate-900 placeholder:text-slate-300 focus:bg-white transition-all outline-none focus:ring-2 focus:ring-rose-500/10" 
                            value={searchTerm} 
                            onChange={e => setSearchTerm(e.target.value)} 
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                                <th className="px-8 py-5 min-w-[240px]">{at(lang, 'personnel')}</th>
                                <th className="px-6 py-5 min-w-[140px]">{at(lang, 'accessLevel')}</th>
                                <th className="px-6 py-5 min-w-[140px]">{at(lang, 'recruitmentDate')}</th>
                                <th className="px-8 py-5 text-right min-w-[180px]">{at(lang, 'ops')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {paginatedUsers.map(u => (
                                <tr key={u.id} className="md:hover:bg-slate-50/50 transition-colors group">
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-4 cursor-pointer" onClick={() => handleViewUserDetail(u)}>
                                            <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-white shadow-sm shrink-0 bg-slate-100 flex items-center justify-center">
                                                {u.avatar ? (
                                                    <img src={u.avatar} className="w-full h-full object-cover" alt={u.name} />
                                                ) : (
                                                    <UserIcon size={20} className="text-slate-300" />
                                                )}
                                            </div>
                                            <div className="flex flex-col min-w-0">
                                                <span className="text-sm font-black text-slate-900 truncate uppercase md:group-hover:text-rose-500 transition-colors">{u.name}</span>
                                                <span className="text-[10px] text-slate-400 font-bold truncate">{u.email}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6">
                                        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${u.role === 'admin' ? 'bg-slate-900 text-white shadow-md' : 'bg-slate-100 text-slate-500'}`}>
                                            <div className={`w-1.5 h-1.5 rounded-full ${u.role === 'admin' ? 'bg-rose-500 animate-pulse' : 'bg-slate-400'}`} />
                                            {u.role || 'user'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-6 font-black text-slate-500 text-[11px] tabular-nums tracking-tighter">
                                        {u.joinDate ? new Date(u.joinDate).toLocaleDateString() : '-'}
                                    </td>
                                    <td className="px-8 py-6 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button onClick={() => openConfirm('AWARD_POINTS', u)} className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-amber-500 md:hover:border-amber-500 rounded-xl transition-all shadow-sm active:scale-90" title={at(lang, 'awardPoints')}><Coins size={16}/></button>
                                            <button onClick={() => handleViewUserDetail(u)} className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-slate-900 md:hover:border-slate-900 rounded-xl transition-all shadow-sm active:scale-90" title={at(lang, 'auditLog')}><History size={16}/></button>
                                            <button onClick={() => openConfirm('UPDATE_ROLE', u)} className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-emerald-600 md:hover:border-emerald-600 rounded-xl transition-all shadow-sm active:scale-90" title={at(lang, 'modifyPerms')}><SettingsIcon size={16}/></button>
                                            <button onClick={() => openConfirm('DELETE_USER', u)} className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-rose-600 md:hover:border-rose-600 rounded-xl transition-all shadow-sm active:scale-90" title={at(lang, 'terminateUser')}><Trash2 size={16}/></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {paginatedUsers.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-8 py-20 text-center">
                                        <div className="flex flex-col items-center justify-center opacity-30 gap-4">
                                            <UserIcon size={48} className="text-slate-200" />
                                            <p className="text-sm font-black uppercase tracking-widest">{at(lang, 'noResultsFound')}</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                
                <div className="p-8 border-t border-slate-100 flex items-center justify-between bg-slate-50/20">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Page {userCurrentPage} of {totalUserPages || 1}</p>
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => setUserCurrentPage(p => Math.max(1, p - 1))}
                            disabled={userCurrentPage === 1}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button 
                            onClick={() => setUserCurrentPage(p => Math.min(totalUserPages, p + 1))}
                            disabled={userCurrentPage === totalUserPages || totalUserPages === 0}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
